import React from "react";
import PageLayout, { SectionReveal } from "../components/PageLayout";
import PageHero from "../components/PageHero";
import PageBanner from "../components/PageBanner";
import { Headphones, BarChart3, Users, TrendingUp, Clock, Shield } from "lucide-react";

const ContactCenterPage = () => {
  const benefits = [
    { icon: Clock, title: "7/24 Kesintisiz Hizmet", desc: "Tatil, mesai, gece yarısı fark etmez. Sesli AI ajanlarınız her an hazır." },
    { icon: TrendingUp, title: "%60'a Varan Maliyet Tasarrufu", desc: "İnsan temsilci maliyetlerine kıyasla çok daha düşük operasyonel gider." },
    { icon: BarChart3, title: "Anlık Analitik", desc: "Her görüşmenin duygu analizi, süresi, çözüm oranı ve müşteri memnuniyet skoru." },
    { icon: Users, title: "Sınırsız Ölçeklenebilirlik", desc: "Aynı anda yüzlerce çağrıyı karşılayın. Mevsimsel yoğunluklara anında adapte olun." },
    { icon: Shield, title: "Tutarlı Kalite", desc: "Her müşteri aynı kalitede hizmet alır. İnsan hatası, ruh hali veya yorgunluk yok." },
  ];

  const hero = (
    <PageHero
      eyebrow="Ürün"
      title="Çağrı Merkezi Çözümü"
      subtitle="Geleneksel çağrı merkezinizi AI destekli, ölçeklenebilir ve 7/24 çalışan modern bir iletişim merkezine dönüştürün."
    />
  );

  return (
    <PageLayout hero={hero}>
      <PageBanner src="/images/blog/call-center-agent.jpg" alt="Çağrı merkezi" height="360px" />
      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[28px] font-bold mb-4" style={{ color: "var(--fg)" }}>Çağrı Merkezinizi Yapay Zeka ile Dönüştürün</h2>
            <p className="text-[15px] leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--fg)", opacity: 0.85 }}>
              İster 5 ister 500 kişilik bir çağrı merkeziniz olsun, Verasist ile rutin çağrıları AI ajanlarınıza devredin. İnsan temsilcileriniz karmaşık ve değerli görüşmelere odaklansın.
            </p>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[28px] font-bold mb-10 text-center" style={{ color: "var(--fg)" }}>Avantajlar</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((b) => (
                <div key={b.title} className="p-6 rounded-2xl transition-colors duration-200" style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(75,111,212,0.12)", color: "var(--brand-cyan)" }}>
                    <b.icon size={22} />
                  </div>
                  <h3 className="text-[16px] font-semibold mb-2" style={{ color: "var(--fg)" }}>{b.title}</h3>
                  <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.82 }}>{b.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <img
                src="/images/pages/business-analytics.jpg"
                alt="Çağrı merkezi analitiği"
                className="w-full h-auto object-cover"
                style={{ maxHeight: "340px" }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[28px] font-bold mb-10 text-center" style={{ color: "var(--fg)" }}>Nasıl Çalışır?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: "01", title: "Entegrasyon", desc: "Mevcut telefon altyapınıza ve CRM'inize bağlanıyoruz. Çoğu durumda saatler içinde entegrasyon tamamlanır." },
                { step: "02", title: "Senaryo Tasarımı", desc: "İş akışlarınızı birlikte tasarlıyoruz. Hazır şablonlarımızdan seçin veya sıfırdan özel senaryo oluşturun." },
                { step: "03", title: "Yayına Alma", desc: "Test, optimizasyon ve canlıya geçiş. Ortalama 7-14 iş günü içinde tamamen operasyonel hale gelirsiniz." },
              ].map((s) => (
                <div key={s.step} className="text-center p-6 rounded-2xl transition-colors duration-200" style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-[14px] font-bold" style={{ background: "rgba(75,111,212,0.12)", color: "var(--brand-cyan)" }}>
                    {s.step}
                  </div>
                  <h3 className="text-[16px] font-semibold mb-2" style={{ color: "var(--fg)" }}>{s.title}</h3>
                  <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.82 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6 text-center">
          <a href="https://app.verasist.ai" className="inline-block px-10 py-4 rounded-full font-bold text-[15px] transition-all duration-200" style={{ background: "var(--brand-navy)", color: "#fff" }}>
            <Headphones size={18} className="inline mr-2" />Demo Talep Edin
          </a>
        </section>
      </SectionReveal>
    </PageLayout>
  );
};

export default ContactCenterPage;
